import {ApolloLink, InMemoryCache} from '@apollo/client/core';
import {onError} from '@apollo/client/link/error';
import {HttpLink} from 'apollo-angular/http';

import allInterfaces from 'frontend/cimclient/graphql/generated/CimSchema';
import {ErrorsService} from 'frontend/services/errors_service';
import {FeedbackAndAnalyticsService} from 'frontend/services/feedback_and_analytics_service';

/**
 * Creates Apollo Client options with the provided dependencies.
 *
 * This factory function sets up the Apollo Client with an in-memory cache, error handling,
 * and a link to the GraphQL endpoint. It uses `HttpLink` for network requests,
 * and `ErrorsService` to report errors.
 *
 * This is a factory function rather than a complete provider, allowing each app
 * to inject its own GraphQL URL.
 *
 * @param httpLink - The HttpLink instance for making GraphQL HTTP requests
 * @param graphqlUrl - The GraphQL endpoint URL
 * @param errorsService - The ErrorsService instance for handling errors
 * @param feedbackAndAnalyticsService - The FeedbackAndAnalyticsService instance for analytics
 * @returns Apollo Client options object
 */
export function createApolloOptions(
  httpLink: HttpLink,
  graphqlUrl: string,
  errorsService: ErrorsService,
  feedbackAndAnalyticsService: FeedbackAndAnalyticsService,
) {
  return {
    cache: new InMemoryCache({
      possibleTypes: allInterfaces.possibleTypes,
    }),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
    link: ApolloLink.from([
      onError(({graphQLErrors, networkError, operation}) => {
        // Allow individual operations (e.g., background model readiness polling) to opt out of
        // global error reporting when they handle expected errors locally.
        if (operation.getContext().skipGlobalErrorHandling) {
          return;
        }
        const errors: string[] = [];
        if (graphQLErrors) {
          errors.push(...graphQLErrors.map(({message}) => `[GraphQL error]: ${message}`));
        }
        if (networkError) {
          errors.push(`[Network error]: ${networkError.message}`);
        }
        if (errors.length > 0) {
          errorsService.setError(errors.join('\n\n'), feedbackAndAnalyticsService);
        }
      }),
      httpLink.create({
        uri: graphqlUrl,
      }),
    ]),
  };
}
